/* eslint-disable indent */
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Award, ChartColumnBigIcon, Edit, Flame, Image as ImageIcon, Lock, Save, User, Users } from "lucide-react";
import { useRouter } from "next/router";
import AchievementDashboard from "@/components/molecules/AchievementDashboard";
import AchievementSection from "@/components/molecules/AchievementSection";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Span } from "@/utils/styling/typography.styled";
import UserCard from "components/atoms/UserCard";
import ImageSelectionModal from "components/molecules/ImageSelectionModal";
import StatisticsDisplay from "components/molecules/StatisticsDisplay";
import ImageGallery from "components/organisms/ImageGallery";
import { ProfilePage as Profile } from "components/organisms/Profile";
import { checkUsernameAvailability, fetchCompletedTasks, followUser, getCurrentUserInfo, getImage, getUserInfo, getUserStats, unfollowUser, updateUser, updateUserProfile } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, IUserDTO, TaskDTO, UserPremiumType } from "types";
import { colourScheme } from "utils/styling/colours";
import { AvatarContainer, AvatarImage, AvatarWrapper, BioSection, BioTextField, BioTitle, ButtonContainer, CancelButton, Container, DefaultAvatar, EditAvatarButton, EditButton, FollowingGrid, Header, HeaderContainer, ProfileInfo, TabItem, TabList, TabsContainer, UserInfo, Username } from "./ProfileLanding.styled";
interface ProfilePageProps {
  user?: IUserDTO;
}
interface UserUpdates {
  username?: string;
}
const ProfilePage: React.FC<ProfilePageProps> = props => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const router = useRouter();
  const {
    username
  } = router.query as {
    username?: string;
  };
  const [userState, setUserState] = useState<IUserDTO | undefined>(props.user);
  const [isEditing, setIsEditing] = useState(false);
  const [bio, setBio] = useState(props.user?.bio || "");
  const [avatarId, setAvatarId] = useState<string | undefined>();
  const [activeTab, setActiveTab] = useState("gallery");
  const [isFollowing, setIsFollowing] = useState(false);

  // This would typically come from your authentication system
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [originalBio, setOriginalBio] = useState(bio);
  const [originalAvatarId, setOriginalAvatarId] = useState(avatarId);
  const [originalAvatarUrl, setOriginalAvatarUrl] = useState(userState?.avatarUrl);
  const [tasks, setTasks] = useState<TaskDTO[]>([]);
  const [isUsernameValid, setIsUsernameValid] = useState<boolean>(true);
  const [newUsername, setNewUsername] = useState<string>(userState?.username || "");
  const subscriptionStyles = {
    NONE: {
      borderColor: colourScheme.greyLight,
      animation: "none"
    },
    BASIC: {
      borderColor: colourScheme.secondaryDark,
      animation: "none"
    },
    PLUS: {
      borderColor: colourScheme.errorDark,
      animation: "subtlePulse"
    },
    PRO: {
      borderColor: colourScheme.alert,
      animation: "subtlePulse"
    },
    ELITE: {
      gradientBorder: `linear-gradient(45deg, ${colourScheme.success}, ${colourScheme.alert}, ${colourScheme.success})`,
      animation: "subtlePulse"
    },
    ULTIMATE: {
      gradientBorder: `linear-gradient(45deg, ${colourScheme.secondaryDark}, ${colourScheme.errorDark}, ${colourScheme.alert})`,
      animation: "subtlePulse 3s infinite, gradientShift 5s linear infinite"
    }
  };
  const subscriptionStyle = subscriptionStyles[userState?.subscriptionType ?? UserPremiumType.NONE] || subscriptionStyles[UserPremiumType.NONE];
  const fetchUserStats = async (userId: string) => {
    try {
      const stats = await getUserStats(userId); // Implement this in `lib/actions`
      if (stats && !("error" in stats)) {
        // Check if no error was returned
        setUserState(prevState => {
          if (prevState) {
            return {
              ...prevState,
              stats: {
                ...prevState.stats,
                ...stats // Merge the new stats with the existing ones
              }
            };
          }
          return prevState;
        });
      } else {
        console.error("Error fetching stats:", stats.error);
      }
    } catch (error) {
      console.error("Error fetching user stats:", error);
    }
  };
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        if (userState?.userId) {
          const response = await fetchCompletedTasks(userState.userId);
          if (response.tasks) {
            setTasks(response.tasks);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    if (userState) {
      fetchTasks();
    }
  }, [userState]);
  useEffect(() => {
    if (activeTab === "stats" && userState?.userId) {
      fetchUserStats(userState.userId); // Fetch stats only when needed
    }
  }, [activeTab, userState?.userId]);
  const handleUsernameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      value
    } = e.target;
    setNewUsername(value);
  }, []);
  const validateUsername = async () => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    if (!userState?.username || userState?.username === newUsername) {
      setIsUsernameValid(true);
      return;
    }
    const isValid = await checkUsernameAvailability(newUsername);
    setIsUsernameValid(isValid);
  };
  useEffect(() => {
    // Determine if the current user is following the profile user
    if (state.isLoggedIn && userState && userState.followers) {
      const isUserFollowing = userState.followers.some(follower => follower.username === state.username);
      setIsFollowing(isUserFollowing);
    }
  }, [state.isLoggedIn, state.username, userState]);
  useEffect(() => {
    const fetchUserData = async () => {
      if (username) {
        try {
          const response = await getUserInfo(username);
          if (response.user) {
            setUserState(response.user);
            setBio(response.user.bio || "");
            setNewUsername(response.user.username);
            // Update other state variables as needed
          } else {
            console.error("User not found");
            // Optionally handle user not found, e.g., redirect or show an error message
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }
    };

    // Fetch data if the username has changed and is different from the current userState
    if (username && username !== userState?.username) {
      fetchUserData();
    }
  }, [userState?.username, username]);
  useEffect(() => {
    if (state.isLoggedIn && state.user && userState) {
      const isUserFollowing = state.user.following?.some(followingUser => followingUser.userId === userState.userId);
      setIsFollowing(!!isUserFollowing);
    } else {
      setIsFollowing(false);
    }
  }, [state.isLoggedIn, state.user, userState]);
  const cancelEdit = () => {
    // Reset editable fields to their original values
    setBio(originalBio);
    setAvatarId(originalAvatarId);
    setUserState(prevState => ({
      ...prevState!,
      avatarUrl: originalAvatarUrl
    }));
    setIsEditing(false);
  };
  const toggleFollow = async () => {
    if (!state.isLoggedIn) {
      // Prompt user to log in
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    try {
      if (isFollowing) {
        // Unfollow the user
        if (userState) {
          const response = await unfollowUser(userState.userId);
          if (response.success) {
            setIsFollowing(false);
            // Optionally update userState.followers
          } else {
            console.error("Error unfollowing user:", response.error);
          }
        }
      } else {
        // Follow the user
        if (userState) {
          const response = await followUser(userState.userId);
          if (response.success) {
            setIsFollowing(true);
            // Optionally update userState.followers
          } else {
            console.error("Error following user:", response.error);
          }
        }
      }
    } catch (error) {
      console.error("Error toggling follow state:", error);
    }
  };

  // Function to open the avatar modal
  const openAvatarModal = () => {
    setIsAvatarModalOpen(true);
  };

  // Function to close the avatar modal
  const closeAvatarModal = () => {
    setIsAvatarModalOpen(false);
  };
  const handleAvatarSelect = async (imageId: string) => {
    if (imageId) {
      setAvatarId(imageId);
      // Update the avatarUrl in userState immediately
      const image = await getImage(imageId); // Implement this function
      if (image.image) setUserState(prevState => ({
        ...prevState!,
        avatarUrl: image.image?.image
      }));
      setIsAvatarModalOpen(false);
    }
  };

  // Function to save profile changes
  const saveProfile = async () => {
    // Implement save profile logic here
    try {
      if (newUsername && newUsername !== userState?.username) {
        await validateUsername();
        if (isUsernameValid) {
          try {
            const updates: UserUpdates = {};
            updates.username = newUsername;
            await updateUser(updates);
          } catch (error) {
            toast.error("Failed to Update Username!", {
              position: "bottom-center"
            });
          }
        } else {
          toast.error("Username is already taken!", {
            position: "bottom-center"
          });
        }
      }
      const response = await updateUserProfile({
        bio,
        avatarId: avatarId
      });
      if (response.user) {
        setUserState(response.user); // Update userState with new data
        setIsEditing(false);
      } else if (response.error) {
        console.error("Failed to update user profile:", response.error);
      }
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };
  const toggleEdit = () => {
    if (!isEditing) {
      // Entering editing mode: store original values
      setOriginalBio(bio);
      setOriginalAvatarId(avatarId);
      setOriginalAvatarUrl(userState?.avatarUrl);
    }
    setIsEditing(!isEditing);
  };
  useEffect(() => {
    if (state.isLoggedIn && userState) {
      setIsOwnProfile(state.username === userState.username);
    } else {
      setIsOwnProfile(false);
    }
  }, [state.isLoggedIn, state.username, userState]);
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!userState) {
        try {
          const response = await getCurrentUserInfo();
          if (response.user) {
            setUserState(response.user);
            setBio(response.user.bio || "");
            setNewUsername(response.user.username);
          } else {
            router.replace("/login");
          }
        } catch (error) {
          console.error("Error fetching user info:", error);
          router.replace("/login");
        }
      }
    };
    fetchUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const defaultFilters = useMemo(() => userState?.userId ? {
    userId: userState.userId
  } : {
    userId: ""
  }, [userState?.userId]);
  if (!userState) {
    // Optionally, you can render a loading state here
    return <div>Loading...</div>;
  }
  return <Container suppressHydrationWarning data-sentry-element="Container" data-sentry-component="ProfilePage" data-sentry-source-file="index.tsx">
            <HeaderContainer data-sentry-element="HeaderContainer" data-sentry-source-file="index.tsx">
                <Header data-sentry-element="Header" data-sentry-source-file="index.tsx">
                    <ProfileInfo data-sentry-element="ProfileInfo" data-sentry-source-file="index.tsx">
                        <AvatarContainer data-sentry-element="AvatarContainer" data-sentry-source-file="index.tsx">
                            <AvatarWrapper subscriptionstyle={subscriptionStyle} data-sentry-element="AvatarWrapper" data-sentry-source-file="index.tsx">
                                {userState.avatarUrl ? <AvatarImage src={userState.avatarUrl} alt="Profile picture" /> : <DefaultAvatar>
                                        <User size={48} color={colourScheme.fullWhite} />
                                    </DefaultAvatar>}
                            </AvatarWrapper>
                            {isEditing && <EditAvatarButton onClick={openAvatarModal} aria-label="Edit Avatar">
                                    <Edit size={16} />
                                </EditAvatarButton>}
                        </AvatarContainer>
                        <div className="flex-grow">
                            {isEditing ? <>
                                    <Input id="username" name="username" onChange={handleUsernameChange} value={newUsername} minLength={3} maxLength={255} placeholder={userState.username} />
                                    {!isUsernameValid && <Span style={{
                color: colourScheme.error
              }}>
                                            This username is taken.
                                        </Span>}
                                </> : <UserInfo>
                                    <Username>{userState.username}</Username>
                                </UserInfo>}
                            <div className="flex items-center mt-2 gap-2">
                                {userState.subscriptionType && <div className="text-xs font-medium px-2 py-0.5 rounded-full inline-block" style={{
                backgroundImage: userState.subscriptionType === UserPremiumType.ELITE ? `linear-gradient(45deg, ${colourScheme.success}, ${colourScheme.alert}, ${colourScheme.successDark})` : userState.subscriptionType === UserPremiumType.ULTIMATE ? `linear-gradient(45deg, ${colourScheme.secondaryDark}, ${colourScheme.errorDark}, ${colourScheme.alert})` : "none",
                backgroundColor: userState.subscriptionType === UserPremiumType.BASIC ? colourScheme.secondaryDark : userState.subscriptionType === UserPremiumType.PLUS ? colourScheme.errorDark : userState.subscriptionType === UserPremiumType.PRO ? colourScheme.alert : userState.subscriptionType === UserPremiumType.NONE ? colourScheme.greyLight : "transparent",
                color: colourScheme.fullWhite
              }}>
                                        {userState.subscriptionType.toLowerCase().charAt(0).toUpperCase() + userState.subscriptionType.toLowerCase().slice(1)}
                                    </div>}

                                <TooltipProvider data-sentry-element="TooltipProvider" data-sentry-source-file="index.tsx">
                                    <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                        <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                            <div className="flex items-center rounded-full" style={{
                      color: colourScheme.errorDark
                    }}>
                                                <Flame className="h-4 w-4 flex-shrink-0" data-sentry-element="Flame" data-sentry-source-file="index.tsx" />
                                                <span className="text-sm mt-0.5 leading-none">
                                                    {userState.streak}
                                                </span>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">
                                            <p>Current Login Streak</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        </div>
                    </ProfileInfo>
                    {isOwnProfile ? isEditing ? <ButtonContainer>
                                <EditButton onClick={saveProfile}>
                                    <Save />
                                    Save Profile
                                </EditButton>
                                <CancelButton onClick={cancelEdit}>
                                    Cancel
                                </CancelButton>
                            </ButtonContainer> : <EditButton onClick={toggleEdit}>
                                <Edit />
                                Edit Profile
                            </EditButton> : <EditButton onClick={toggleFollow}>
                            {isFollowing ? "Unfollow" : "Follow"}
                        </EditButton>}
                </Header>

                <BioSection data-sentry-element="BioSection" data-sentry-source-file="index.tsx">
                    <BioTitle data-sentry-element="BioTitle" data-sentry-source-file="index.tsx">Bio</BioTitle>
                    {isEditing ? <BioTextField as="textarea" rows={4} value={bio} onChange={e => setBio(e.target.value)} /> : bio ? <p>{bio}</p> : <p className="placeholder-bio">
                            This user hasn&apos;t added a bio yet.
                        </p>}
                </BioSection>
                <AchievementSection achievements={tasks} data-sentry-element="AchievementSection" data-sentry-source-file="index.tsx" />
            </HeaderContainer>
            <TabsContainer data-sentry-element="TabsContainer" data-sentry-source-file="index.tsx">
                <TabList data-sentry-element="TabList" data-sentry-source-file="index.tsx">
                    <TabItem isActive={activeTab === "gallery"} onClick={() => setActiveTab("gallery")} data-sentry-element="TabItem" data-sentry-source-file="index.tsx">
                        <ImageIcon color="white" data-sentry-element="ImageIcon" data-sentry-source-file="index.tsx" /> Gallery
                    </TabItem>
                    <TabItem isActive={activeTab === "following"} onClick={() => setActiveTab("following")} data-sentry-element="TabItem" data-sentry-source-file="index.tsx">
                        <Users color="white" data-sentry-element="Users" data-sentry-source-file="index.tsx" /> Following
                    </TabItem>
                    <TabItem isActive={activeTab === "followers"} onClick={() => setActiveTab("followers")} data-sentry-element="TabItem" data-sentry-source-file="index.tsx">
                        <Users color="white" data-sentry-element="Users" data-sentry-source-file="index.tsx" /> Followers
                    </TabItem>
                    <TabItem isActive={activeTab === "stats"} onClick={() => setActiveTab("stats")} data-sentry-element="TabItem" data-sentry-source-file="index.tsx">
                        <ChartColumnBigIcon color="white" data-sentry-element="ChartColumnBigIcon" data-sentry-source-file="index.tsx" /> Stats
                    </TabItem>
                    <TabItem isActive={activeTab === "achievements"} onClick={() => setActiveTab("achievements")} data-sentry-element="TabItem" data-sentry-source-file="index.tsx">
                        <Award color="white" data-sentry-element="Award" data-sentry-source-file="index.tsx" /> Achievements
                    </TabItem>
                    {isOwnProfile && <TabItem isActive={activeTab === "private"} onClick={() => setActiveTab("private")}>
                            <Lock color="white" /> Private
                        </TabItem>}
                </TabList>

                {activeTab === "gallery" && <ImageGallery gallerySubtitle={`${userState.username}'s Gallery`} userGallery={isOwnProfile} defaultFilters={defaultFilters} />}

                {activeTab === "following" && <FollowingGrid>
                        {userState.following?.map(followedUser => <UserCard key={followedUser.userId} user={followedUser} />)}
                    </FollowingGrid>}

                {activeTab === "followers" && <FollowingGrid>
                        {userState.followers?.map(followedUser => <UserCard key={followedUser.userId} user={followedUser} />)}
                    </FollowingGrid>}
                {activeTab === "stats" && userState.stats && <StatisticsDisplay stats={userState.stats} />}

                {activeTab === "achievements" && <AchievementDashboard />}

                {isOwnProfile && activeTab === "private" && <Profile />}
            </TabsContainer>
            <ImageSelectionModal open={isAvatarModalOpen} onClose={closeAvatarModal} onSelect={handleAvatarSelect} userGallery={isOwnProfile} defaultFilters={{
      userId: userState.userId
    }} data-sentry-element="ImageSelectionModal" data-sentry-source-file="index.tsx" />
        </Container>;
};
export default ProfilePage;